import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { isSubscriptionsEnabled } from '@/utils/featureFlags'
import { ROUTES } from '../routes'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * ProtectedRoute Guard
 *
 * This component replicates the exact authentication and onboarding logic
 * from the original App.tsx (lines 241-312).
 *
 * Checks performed in order:
 * 1. Show loading state while checking auth
 * 2. Redirect to login if not authenticated
 * 3. Check subscription status (canceled/suspended → logout + redirect)
 * 4. Check onboarding completion (redirect to appropriate step)
 * 5. Allow access if all checks pass
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore()

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary" />
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    )
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // If user is authenticated, perform additional checks
  if (isAuthenticated && user) {
    const { onboardingStatus, roleName, subscriptionStatus } = user

    // System admins bypass all onboarding and subscription checks
    const isSystemAdmin = roleName === 'system_admin'
    const isOnboardingComplete =
      onboardingStatus === 'completed' || onboardingStatus === 'admin_created'

    // Check for canceled or suspended subscription status
    // Block access and redirect to login page
    // Skip this check when subscriptions feature is disabled
    if (
      isSubscriptionsEnabled() &&
      !isSystemAdmin &&
      (subscriptionStatus === 'canceled' || subscriptionStatus === 'suspended')
    ) {
      // Clear auth state and redirect to login
      useAuthStore.getState().logout()
      return <Navigate to={ROUTES.LOGIN} replace />
    }

    // Check onboarding completion and redirect if incomplete
    // Skip onboarding checks when subscriptions feature is disabled (legacy flow)
    if (
      isSubscriptionsEnabled() &&
      !isSystemAdmin &&
      !isOnboardingComplete
    ) {

      // Redirect based on onboarding status
      switch (onboardingStatus) {
        case 'pending_verification':
          return <Navigate to={ROUTES.ONBOARDING.VERIFY_EMAIL} replace />

        case 'verified_unpaid':
          return <Navigate to={ROUTES.ONBOARDING.CHECKOUT} replace />

        case 'payment_pending':
          return <Navigate to={ROUTES.ONBOARDING.SUCCESS} replace />

        default:
          // Unknown or null status - treat as complete and allow access
          // This handles legacy users or backend not returning onboardingStatus
          console.warn(
            '[ProtectedRoute] Unknown onboarding status, allowing access:',
            onboardingStatus
          )
          break
      }
    }
  }

  // All checks passed, render protected content
  return <>{children}</>
}
